import { Schema } from "effect";
import type { ProviderKind } from "./orchestration";

export const CODEX_REASONING_EFFORT_OPTIONS = [
  "ultra",
  "max",
  "xhigh",
  "high",
  "medium",
  "low",
] as const;
export type CodexReasoningEffort = (typeof CODEX_REASONING_EFFORT_OPTIONS)[number];
export const CLAUDE_CODE_EFFORT_OPTIONS = [
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
  "ultrathink",
] as const;
export type ClaudeCodeEffort = (typeof CLAUDE_CODE_EFFORT_OPTIONS)[number];
export type ProviderReasoningEffort = CodexReasoningEffort | ClaudeCodeEffort;

export const CodexModelOptions = Schema.Struct({
  reasoningEffort: Schema.optional(Schema.Literals(CODEX_REASONING_EFFORT_OPTIONS)),
  fastMode: Schema.optional(Schema.Boolean),
});
export type CodexModelOptions = typeof CodexModelOptions.Type;

export const ClaudeModelOptions = Schema.Struct({
  thinking: Schema.optional(Schema.Boolean),
  effort: Schema.optional(Schema.Literals(CLAUDE_CODE_EFFORT_OPTIONS)),
  fastMode: Schema.optional(Schema.Boolean),
});
export type ClaudeModelOptions = typeof ClaudeModelOptions.Type;

export const ProviderModelOptions = Schema.Struct({
  codex: Schema.optional(CodexModelOptions),
  claudeAgent: Schema.optional(ClaudeModelOptions),
});
export type ProviderModelOptions = typeof ProviderModelOptions.Type;

export type EffortOption = {
  readonly value: string;
  readonly label: string;
  readonly isDefault?: true;
};

export type ModelCapabilities = {
  readonly reasoningEffortLevels: readonly EffortOption[];
  readonly supportsFastMode: boolean;
  readonly supportsThinkingToggle: boolean;
  readonly promptInjectedEffortLevels: readonly string[];
};

type ModelDefinition = {
  readonly slug: string;
  readonly name: string;
  readonly capabilities: ModelCapabilities;
  readonly contextWindow?: number;
};

const CODEX_EFFORT_LABELS: Record<CodexReasoningEffort, string> = {
  ultra: "Ultra",
  max: "Max",
  xhigh: "Extra High",
  high: "High",
  medium: "Medium",
  low: "Low",
};

const CLAUDE_EFFORT_LABELS: Record<ClaudeCodeEffort, string> = {
  ultrathink: "Ultrathink",
  max: "Max",
  xhigh: "Extra High",
  high: "High",
  medium: "Medium",
  low: "Low",
};

/**
 * Build an effort ladder, marking `defaultLevel` as the provider's own default.
 *
 * Every model repeats the same handful of ladders, so spelling them out per
 * entry made the catalog hard to keep truthful: the levels and the default are
 * per-model facts that drift on each model release.
 */
const effortLevels = <Effort extends string>(
  labels: Record<Effort, string>,
  levels: readonly Effort[],
  defaultLevel: Effort,
): readonly EffortOption[] =>
  levels.map((value) => ({
    value,
    label: labels[value],
    ...(value === defaultLevel ? { isDefault: true as const } : {}),
  }));

const codexEfforts = (
  levels: readonly CodexReasoningEffort[],
  defaultLevel: CodexReasoningEffort,
) => effortLevels(CODEX_EFFORT_LABELS, levels, defaultLevel);

/**
 * Claude ladders always end with `ultrathink`, which is not an API effort level
 * but a prompt keyword (see `promptInjectedEffortLevels`), so it never carries
 * the default.
 */
const claudeEfforts = (levels: readonly ClaudeCodeEffort[], defaultLevel: ClaudeCodeEffort) =>
  effortLevels(CLAUDE_EFFORT_LABELS, [...levels, "ultrathink"], defaultLevel);

/**
 * TODO: This should not be a static array, each provider
 * should return its own model list over the WS API.
 *
 * Codex already answers `model/list` over the app-server (see
 * `codexAppServerManager.listModels`), and that response — not this table — is
 * what the account actually accepts. Keep the two in step: every Codex entry
 * below mirrors a model the app-server returns, including its effort ladder and
 * per-model default effort.
 */
export const MODEL_OPTIONS_BY_PROVIDER = {
  codex: [
    {
      slug: "gpt-6-astra",
      name: "GPT-6 Astra",
      capabilities: {
        // `low` is the app-server's own default for Astra, not a cost choice of
        // ours: the model is tuned to work at the bottom of the ladder.
        reasoningEffortLevels: codexEfforts(
          ["ultra", "max", "xhigh", "high", "medium", "low"],
          "low",
        ),
        supportsFastMode: true,
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: [],
      },
    },
    {
      slug: "gpt-5.6-sol",
      name: "GPT-5.6 Sol",
      capabilities: {
        // Sol is "highly capable at lower reasoning efforts", hence the low default.
        reasoningEffortLevels: codexEfforts(
          ["ultra", "max", "xhigh", "high", "medium", "low"],
          "low",
        ),
        supportsFastMode: true,
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: [],
      },
    },
    {
      slug: "gpt-5.6-terra",
      name: "GPT-5.6 Terra",
      capabilities: {
        reasoningEffortLevels: codexEfforts(
          ["ultra", "max", "xhigh", "high", "medium", "low"],
          "medium",
        ),
        supportsFastMode: true,
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: [],
      },
    },
    {
      slug: "gpt-5.6-luna",
      name: "GPT-5.6 Luna",
      capabilities: {
        // No `ultra`: task delegation is Sol/Terra only.
        reasoningEffortLevels: codexEfforts(["max", "xhigh", "high", "medium", "low"], "medium"),
        supportsFastMode: true,
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: [],
      },
    },
    {
      slug: "gpt-5.5",
      name: "GPT-5.5",
      capabilities: {
        reasoningEffortLevels: codexEfforts(["xhigh", "high", "medium", "low"], "medium"),
        supportsFastMode: true,
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: [],
      },
    },
  ],
  claudeAgent: [
    {
      slug: "claude-fable-5-1",
      name: "Claude Fable 5.1",
      contextWindow: 1_000_000,
      capabilities: {
        reasoningEffortLevels: claudeEfforts(["low", "medium", "high", "xhigh", "max"], "high"),
        // Fast mode is an Opus-only feature.
        supportsFastMode: false,
        // Thinking cannot be turned off on the Fable line at all.
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: ["ultrathink"],
      },
    },
    {
      slug: "claude-fable-5",
      name: "Claude Fable 5",
      contextWindow: 1_000_000,
      capabilities: {
        reasoningEffortLevels: claudeEfforts(["low", "medium", "high", "xhigh", "max"], "high"),
        // Fast mode is an Opus-only feature.
        supportsFastMode: false,
        // Thinking cannot be turned off on Fable 5 at all.
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: ["ultrathink"],
      },
    },
    {
      slug: "claude-opus-5-5",
      name: "Claude Opus 5.5",
      contextWindow: 1_000_000,
      capabilities: {
        reasoningEffortLevels: claudeEfforts(["low", "medium", "high", "xhigh", "max"], "high"),
        supportsFastMode: true,
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: ["ultrathink"],
      },
    },
    {
      slug: "claude-opus-5",
      name: "Claude Opus 5",
      contextWindow: 1_000_000,
      capabilities: {
        reasoningEffortLevels: claudeEfforts(["low", "medium", "high", "xhigh", "max"], "high"),
        supportsFastMode: true,
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: ["ultrathink"],
      },
    },
    {
      slug: "claude-sonnet-5",
      name: "Claude Sonnet 5",
      contextWindow: 1_000_000,
      capabilities: {
        reasoningEffortLevels: claudeEfforts(["low", "medium", "high", "xhigh", "max"], "high"),
        supportsFastMode: false,
        supportsThinkingToggle: false,
        promptInjectedEffortLevels: ["ultrathink"],
      },
    },
    {
      slug: "claude-haiku-4-5",
      name: "Claude Haiku 4.5",
      contextWindow: 200_000,
      capabilities: {
        // Haiku 4.5 predates adaptive reasoning: no effort, but a thinking toggle.
        reasoningEffortLevels: [],
        supportsFastMode: false,
        supportsThinkingToggle: true,
        promptInjectedEffortLevels: [],
      },
    },
  ],
} as const satisfies Record<ProviderKind, readonly ModelDefinition[]>;
export type ModelOptionsByProvider = typeof MODEL_OPTIONS_BY_PROVIDER;

type BuiltInModelSlug = (typeof MODEL_OPTIONS_BY_PROVIDER)[ProviderKind][number]["slug"];
export type ModelSlug = BuiltInModelSlug | (string & {});

export const DEFAULT_MODEL_BY_PROVIDER: Record<ProviderKind, ModelSlug> = {
  codex: "gpt-5.6-luna",
  claudeAgent: "claude-sonnet-5",
};

// Backward compatibility for existing Codex-only call sites.
export const MODEL_OPTIONS = MODEL_OPTIONS_BY_PROVIDER.codex;
export const DEFAULT_MODEL = DEFAULT_MODEL_BY_PROVIDER.codex;
export const DEFAULT_GIT_TEXT_GENERATION_MODEL = "gpt-5.6-luna" as const;

/**
 * Slugs of models this catalog used to offer, mapped to their replacement.
 *
 * Threads, per-user defaults and project defaults all persist the slug they
 * were created with, so dropping a retired model is only safe while these
 * entries exist: `normalizeModelSlug` rewrites the stored slug before
 * `resolveModelSlug` checks membership. Without them a retired slug misses the
 * catalog and silently falls back to the provider default, which is a
 * different model than the one the user picked.
 *
 * Replacements follow the providers' own guidance where they published one —
 * OpenAI maps 5.4 to Terra and 5.4 mini to Luna.
 */
const RETIRED_CODEX_SLUGS: Record<string, ModelSlug> = {
  "gpt-5.4": "gpt-5.6-terra",
  "gpt-5.4-mini": "gpt-5.6-luna",
  "gpt-5.3-codex": "gpt-5.6-terra",
  "gpt-5.3-codex-spark": "gpt-5.6-luna",
  "gpt-5.2-codex": "gpt-5.6-terra",
  "gpt-5.2": "gpt-5.6-terra",
};

const RETIRED_CLAUDE_SLUGS: Record<string, ModelSlug> = {
  "claude-opus-4-8": "claude-opus-5",
  "claude-opus-4-7": "claude-opus-5",
  "claude-opus-4-6": "claude-opus-5",
  "claude-opus-4-5": "claude-opus-5",
  "claude-sonnet-4-6": "claude-sonnet-5",
  "claude-sonnet-4-5": "claude-sonnet-5",
};

export const MODEL_SLUG_ALIASES_BY_PROVIDER: Record<ProviderKind, Record<string, ModelSlug>> = {
  codex: {
    ...RETIRED_CODEX_SLUGS,
    astra: "gpt-6-astra",
    "6": "gpt-6-astra",
    "gpt-6": "gpt-6-astra",
    "6-astra": "gpt-6-astra",
    "5.6": "gpt-5.6-sol",
    sol: "gpt-5.6-sol",
    "5.6-sol": "gpt-5.6-sol",
    "gpt-5.6": "gpt-5.6-sol",
    terra: "gpt-5.6-terra",
    "5.6-terra": "gpt-5.6-terra",
    luna: "gpt-5.6-luna",
    "5.6-luna": "gpt-5.6-luna",
    "5.5": "gpt-5.5",
    // Legacy shorthands kept pointing at the replacements above.
    "5.4": "gpt-5.6-terra",
    "5.3": "gpt-5.6-terra",
    "gpt-5.3": "gpt-5.6-terra",
    "5.3-spark": "gpt-5.6-luna",
    "gpt-5.3-spark": "gpt-5.6-luna",
  },
  claudeAgent: {
    ...RETIRED_CLAUDE_SLUGS,
    // The bare family name tracks the current release, as `opus` and `sonnet`
    // do; the versioned spellings keep naming the exact model they name.
    fable: "claude-fable-5-1",
    "fable-5.1": "claude-fable-5-1",
    "claude-fable-5.1": "claude-fable-5-1",
    "5.1": "claude-fable-5-1",
    "fable-5": "claude-fable-5",
    "claude-fable-5-20260609": "claude-fable-5",
    opus: "claude-opus-5-5",
    "opus-5.5": "claude-opus-5-5",
    "claude-opus-5.5": "claude-opus-5-5",
    "5.5": "claude-opus-5-5",
    "opus-5": "claude-opus-5",
    "claude-opus-5.0": "claude-opus-5",
    sonnet: "claude-sonnet-5",
    "sonnet-5": "claude-sonnet-5",
    "claude-sonnet-5.0": "claude-sonnet-5",
    haiku: "claude-haiku-4-5",
    "haiku-4.5": "claude-haiku-4-5",
    "claude-haiku-4.5": "claude-haiku-4-5",
    "claude-haiku-4-5-20251001": "claude-haiku-4-5",
    // Legacy shorthands kept pointing at the replacements above.
    "opus-4.7": "claude-opus-5",
    "claude-opus-4.7": "claude-opus-5",
    "claude-opus-4-7-20260416": "claude-opus-5",
    "opus-4.6": "claude-opus-5",
    "claude-opus-4.6": "claude-opus-5",
    "claude-opus-4-6-20251117": "claude-opus-5",
    "sonnet-4.6": "claude-sonnet-5",
    "claude-sonnet-4.6": "claude-sonnet-5",
    "claude-sonnet-4-6-20251117": "claude-sonnet-5",
  },
};

// ── Agent mention aliases ─────────────────────────────────────────────
// Re-exported from agentMentions.ts for backward compatibility
export {
  AGENT_MENTION_ALIASES,
  getAgentMentionAutocompleteAliases,
  getAgentMentionAliases,
  resolveAgentAlias,
  isValidAgentAlias,
  getAgentAliasNames,
  type AgentAliasDefinition,
  type ResolvedAgentAlias,
} from "./agentMentions";

// ── Model capabilities index ──────────────────────────────────────────

export const MODEL_CAPABILITIES_INDEX = Object.fromEntries(
  Object.entries(MODEL_OPTIONS_BY_PROVIDER).map(([provider, models]) => [
    provider,
    Object.fromEntries(models.map((m) => [m.slug, m.capabilities])),
  ]),
) as unknown as Record<ProviderKind, Record<string, ModelCapabilities>>;

// ── Provider display names ────────────────────────────────────────────

export const PROVIDER_DISPLAY_NAMES: Record<ProviderKind, string> = {
  codex: "Codex",
  claudeAgent: "Claude",
};
